import {
  Engine, Scene, Vector3, Color3, Color4,
  ArcRotateCamera, UniversalCamera,
  HemisphericLight, DirectionalLight, PointLight, SpotLight,
  ShadowGenerator, CascadedShadowGenerator,
  DefaultRenderingPipeline,
  CubeTexture, GlowLayer,
  MeshBuilder, Mesh, PBRMaterial, DynamicTexture,
} from '@babylonjs/core';
import { createMaterials } from './materials.js';
import { BuildingGenerator } from './building-generator.js';
import { BUILDING, GROUND_FLOOR, UPPER_FLOOR, PARKING_GROUPS, PLACED_PLANTERS, PLACED_ROADS, GROUND_PAD_OUTLINE } from './building-data.js';
import { FloorPlanEditor } from './floor-plan-editor.js';

// ─── INIT ───
const canvas = document.getElementById('renderCanvas');
const engine = new Engine(canvas, true, {
  preserveDrawingBuffer: true,
  stencil: true,
  antialias: true,
});

const scene = new Scene(engine);
scene.useRightHandedSystem = true;

// ─── DEBUG UTILITIES (use from browser console: dbg.hide('tree'), dbg.show('tree'), etc.) ───
window.dbg = {
  // Hide meshes matching a name pattern
  hide(pattern) {
    const matches = scene.meshes.filter(m => m.name.toLowerCase().includes(pattern.toLowerCase()));
    matches.forEach(m => m.setEnabled(false));
    console.log(`[dbg] Hidden ${matches.length} meshes matching "${pattern}"`);
    return matches.map(m => m.name);
  },
  // Show meshes matching a name pattern
  show(pattern) {
    const matches = scene.meshes.filter(m => m.name.toLowerCase().includes(pattern.toLowerCase()));
    matches.forEach(m => m.setEnabled(true));
    console.log(`[dbg] Shown ${matches.length} meshes matching "${pattern}"`);
    return matches.map(m => m.name);
  },
  // Toggle meshes matching a name pattern
  toggle(pattern) {
    const matches = scene.meshes.filter(m => m.name.toLowerCase().includes(pattern.toLowerCase()));
    matches.forEach(m => m.setEnabled(!m.isEnabled()));
    console.log(`[dbg] Toggled ${matches.length} meshes matching "${pattern}"`);
  },
  // List all unique mesh name prefixes (first word before _)
  list() {
    const groups = {};
    scene.meshes.forEach(m => {
      const prefix = m.name.split('_')[0] || m.name;
      groups[prefix] = (groups[prefix] || 0) + 1;
    });
    console.table(groups);
    return groups;
  },
  // Search meshes by name pattern (just lists, doesn't change anything)
  find(pattern) {
    const matches = scene.meshes.filter(m => m.name.toLowerCase().includes(pattern.toLowerCase()));
    console.log(`[dbg] Found ${matches.length} meshes matching "${pattern}":`);
    matches.slice(0, 30).forEach(m => console.log(`  ${m.name} [enabled=${m.isEnabled()}]`));
    if (matches.length > 30) console.log(`  ... and ${matches.length - 30} more`);
    return matches;
  },
  // Show FPS and scene stats
  fps() {
    const fps = engine.getFps().toFixed(1);
    const verts = scene.getTotalVertices();
    const active = scene.getActiveMeshes().length;
    const total = scene.meshes.length;
    console.log(`[dbg] FPS: ${fps} | Active: ${active}/${total} meshes | Vertices: ${verts}`);
    return { fps, active, total, verts };
  },
  // Hide everything except meshes matching pattern
  solo(pattern) {
    scene.meshes.forEach(m => {
      if (m.name.toLowerCase().includes(pattern.toLowerCase())) {
        m.setEnabled(true);
      } else {
        m.setEnabled(false);
      }
    });
    console.log(`[dbg] Solo: only showing meshes matching "${pattern}"`);
  },
  // Show all meshes (reset)
  showAll() {
    scene.meshes.forEach(m => m.setEnabled(true));
    console.log(`[dbg] All ${scene.meshes.length} meshes shown`);
  },
  scene,
  engine,
}; // Fix mirror-flip so 3D layout matches floor plan
scene.clearColor = new Color4(0.14, 0.30, 0.58, 1.0);
scene.ambientColor = new Color3(0.3, 0.3, 0.35);
scene.fogMode = Scene.FOGMODE_EXP2;
scene.fogDensity = 0.0004;
scene.fogColor = new Color3(0.55, 0.72, 0.9);
scene.environmentIntensity = 1.5;

// ─── ENVIRONMENT (IBL for reflections) ───
scene.environmentTexture = CubeTexture.CreateFromPrefilteredData(
  'https://assets.babylonjs.com/environments/environmentSpecular.env',
  scene
);

// ─── CAMERAS ───
// Restore saved camera state or use defaults
const _savedCam = (() => {
  try { return JSON.parse(localStorage.getItem('bb2_camera')); } catch { return null; }
})();
const orbitalCam = new ArcRotateCamera('orbitalCam',
  _savedCam ? _savedCam.alpha : 1.2100,
  _savedCam ? _savedCam.beta : 1.3535,
  _savedCam ? _savedCam.radius : 215.1,
  _savedCam ? new Vector3(_savedCam.tx, _savedCam.ty, _savedCam.tz) : new Vector3(199.0, 13.8, 286.7),
  scene
);
orbitalCam.lowerRadiusLimit = 2;
orbitalCam.upperRadiusLimit = 800;
orbitalCam.lowerBetaLimit = 0.05;
orbitalCam.upperBetaLimit = Math.PI / 2.05;
orbitalCam.panningSensibility = 25;
orbitalCam.pinchDeltaPercentage = 0.004;
orbitalCam.inertia = 0.0;
orbitalCam.attachControl(canvas, true);

// Fully disable Babylon's built-in wheel zoom — we handle it ourselves
orbitalCam.inputs.attached.mousewheel.detachControl();
orbitalCam.inputs.removeByType('ArcRotateCameraMouseWheelInput');

// Custom cross-browser wheel zoom (Chrome + Firefox identical behavior)
// Normalizes deltaY to pixels, then uses magnitude for proportional zoom.
// Chrome: 1 event, deltaY~100px.  Firefox: deltaMode=1 (lines), deltaY~3 → *33 ≈ 99px.
// Firefox smooth-scroll: many small events that sum to same total.
canvas.addEventListener('wheel', (e) => {
  if (scene.activeCamera !== orbitalCam) return;
  e.preventDefault();
  let dy = e.deltaY;
  if (e.deltaMode === 1) dy *= 33;        // lines → pixels (Firefox)
  else if (e.deltaMode === 2) dy *= window.innerHeight; // pages → pixels
  const sign = Math.sign(dy);
  if (sign === 0) return;
  const pct = e.shiftKey ? 0.15 : 0.05;
  const pctDelta = orbitalCam.radius * pct * Math.abs(dy) / 100;
  const minDelta = e.shiftKey ? 3.0 : 1.0; // minimum absolute step
  orbitalCam.radius += sign * Math.max(pctDelta, minDelta);
  orbitalCam.radius = Math.max(orbitalCam.lowerRadiusLimit,
    Math.min(orbitalCam.upperRadiusLimit, orbitalCam.radius));
}, { passive: false });

const walkCam = new UniversalCamera('walkCam',
  new Vector3(144, 5.6, 195), scene
);
walkCam.speed = 1.3;
walkCam.angularSensibility = 3000;
walkCam.keysUp = [87, 38];
walkCam.keysDown = [83, 40];
walkCam.keysLeft = [65, 37];
walkCam.keysRight = [68, 39];
walkCam.minZ = 0.3;
walkCam.ellipsoid = new Vector3(1, 2.8, 1);
walkCam.checkCollisions = true;
walkCam.applyGravity = true;

let activeCamera = orbitalCam;
scene.activeCamera = orbitalCam;

// ─── LIGHTING ───

// Sky ambient
const ambient = new HemisphericLight('ambient', new Vector3(0.2, 1, 0.1), scene);
ambient.intensity = 0.55;
ambient.diffuse = new Color3(0.92, 0.95, 1.0);  // Bright sky
ambient.groundColor = new Color3(0.35, 0.32, 0.28); // Warm ground bounce
ambient.specular = new Color3(0.1, 0.1, 0.1);

// Sun — warm directional
const sun = new DirectionalLight('sun', new Vector3(-0.6, -1.5, 0.8).normalize(), scene);
sun.intensity = 1.5;
sun.diffuse = new Color3(1.0, 0.95, 0.85);
sun.specular = new Color3(0.9, 0.85, 0.7);
sun.position = new Vector3(200, 260, -130);

// Secondary fill (opposite side, cool)
const fill = new DirectionalLight('fill', new Vector3(0.5, -0.5, -0.8).normalize(), scene);
fill.intensity = 0.3;
fill.diffuse = new Color3(0.7, 0.8, 1.0);
fill.specular = new Color3(0, 0, 0);

// Interior accent lights — reduced count for performance
const interiorLights = [];
for (let floor = 0; floor <= 1; floor++) {
  const elev = floor === 0 ? 8 : 29;
  const pl = new PointLight(`interior_pl_${floor}`, new Vector3(144, elev, 115), scene);
  pl.intensity = 0.2;
  pl.diffuse = new Color3(1.0, 0.92, 0.75);
  pl.range = 130;
  interiorLights.push(pl);
}

// ─── SHADOWS ───
const shadowGen = new ShadowGenerator(1024, sun);
shadowGen.useBlurExponentialShadowMap = true;
shadowGen.blurKernel = 16;
shadowGen.blurScale = 2;
shadowGen.setDarkness(0.3);
shadowGen.bias = 0.001;
shadowGen.normalBias = 0.02;
shadowGen.useKernelBlur = true;
shadowGen.filteringQuality = ShadowGenerator.QUALITY_MEDIUM;

// ─── GLOW LAYER (optimized: smaller blur + skip non-glow meshes) ───
const glowLayer = new GlowLayer('glow', scene, {
  mainTextureSamples: 1,
  mainTextureFixedSize: 512,
  blurKernelSize: 8,
});
glowLayer.intensity = 0.3;
// Only emit glow for lightPanel and warmGlow materials — skip everything else
const _glowMatNames = new Set(['lightPanel', 'warmGlow', 'signageText']);
const _black = new Color4(0, 0, 0, 0);
glowLayer.customEmissiveColorSelector = (mesh, subMesh, material, result) => {
  if (material && _glowMatNames.has(material.name)) {
    result.set(
      material.emissiveColor.r, material.emissiveColor.g,
      material.emissiveColor.b, 1.0
    );
  } else {
    result.set(0, 0, 0, 0);
  }
};

// ─── POST-PROCESSING ───
const cameras = [orbitalCam, walkCam];

const pipeline = new DefaultRenderingPipeline('pipeline', true, scene, cameras);
pipeline.bloomEnabled = true;
pipeline.bloomThreshold = 0.8;
pipeline.bloomWeight = 0.15;
pipeline.bloomKernel = 32;
pipeline.bloomScale = 0.5;
pipeline.fxaaEnabled = true;
pipeline.sharpenEnabled = false;
pipeline.chromaticAberrationEnabled = false;
pipeline.grainEnabled = false;

// Tone mapping
pipeline.imageProcessingEnabled = true;
pipeline.imageProcessing.toneMappingEnabled = true;
pipeline.imageProcessing.toneMappingType = 0; // Standard (less color desaturation than ACES)
pipeline.imageProcessing.contrast = 1.12;
pipeline.imageProcessing.exposure = 1.05;
pipeline.imageProcessing.vignetteEnabled = true;
pipeline.imageProcessing.vignetteWeight = 0.15;
pipeline.imageProcessing.vignetteColor = new Color4(0.04, 0.06, 0.12, 0);

// SSAO removed for performance

// ─── BUILD ───
const materials = createMaterials(scene);
const generator = new BuildingGenerator(scene, materials);

const loadProgress = document.getElementById('loadProgress');
loadProgress.style.width = '30%';

const floorNodes = generator.generateAll();
loadProgress.style.width = '85%';

// Shadow casters — add major exterior geometry (limited for perf)
let shadowCasterCount = 0;
scene.meshes.forEach(m => {
  if (shadowCasterCount > 60) return;
  if (
    m.name.includes('ext') || m.name.includes('parapet') ||
    m.name.includes('canopy') || m.name.includes('trunk') ||
    m.name.includes('sidewalk') || m.name.includes('reception_desk')
  ) {
    shadowGen.addShadowCaster(m);
    shadowCasterCount++;
  }
  // Receive shadows on ground and floor slabs
  if (m.name.includes('ground') || m.name.includes('Floor') || m.name.includes('road') || m.name.includes('sidewalk')) {
    m.receiveShadows = true;
  }
  // Collisions for walkthrough
  if (m.name.includes('wall') || m.name.includes('Floor') || m.name.includes('floor') || m.name.includes('slab')) {
    m.checkCollisions = true;
  }
});

loadProgress.style.width = '100%';

// ─── UNIT LABELS (flat horizontal planes on floor, visible from above) ───
const gfLabelMeshes = [];
const ufLabelMeshes = [];

function createLabel(id, text, x, z, w, d, elev, floorHeight, meshArray) {
  // Size label to fit within unit bounds
  const planeW = w * 0.85;
  const planeH = d * 0.85;
  const plane = MeshBuilder.CreatePlane(`label_${id}`, { width: planeW, height: planeH }, scene);
  // Lay flat on the floor, slightly above to avoid z-fighting
  plane.position = new Vector3(x + w / 2, elev + 1.5, z + d / 2);
  plane.rotation.x = -Math.PI / 2;  // face upward (normal = +Y in RHS)

  // Texture resolution proportional to plane aspect
  const resW = 256;
  const resH = Math.round(resW * planeH / planeW);
  const tex = new DynamicTexture(`labelTex_${id}`, { width: resW, height: resH }, scene, false);
  tex.hasAlpha = true;
  const ctx = tex.getContext();
  ctx.clearRect(0, 0, resW, resH);

  // Semi-transparent dark background
  ctx.fillStyle = 'rgba(20, 20, 30, 0.75)';
  ctx.fillRect(0, 0, resW, resH);

  // Gold border
  ctx.strokeStyle = '#c9a94e';
  ctx.lineWidth = 3;
  ctx.strokeRect(4, 4, resW - 8, resH - 8);

  // Auto-size font to fit text
  const maxFontH = Math.floor(resH * 0.5);
  const maxFontW = Math.floor((resW * 0.85) / (text.length * 0.55));
  const fontSize = Math.max(Math.min(maxFontH, maxFontW, 48), 12);
  ctx.fillStyle = '#c9a94e';
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, resW / 2, resH / 2);
  tex.update();

  const mat = new PBRMaterial(`labelMat_${id}`, scene);
  mat.emissiveTexture = tex;
  mat.emissiveColor = new Color3(1, 1, 1);
  mat.albedoColor = new Color3(0, 0, 0);
  mat.unlit = true;
  mat.backFaceCulling = false;
  mat.opacityTexture = tex;
  mat.transparencyMode = 2;
  plane.material = mat;
  plane.isPickable = false;

  meshArray.push(plane);
}

// Ground floor showroom labels (GF is double-height: 23.83ft)
const gfFloorH = BUILDING.floors['0'].height;
GROUND_FLOOR.showrooms.forEach((sr) => {
  createLabel(sr.id, sr.label, sr.x, sr.z, sr.w, sr.d, 0, gfFloorH, gfLabelMeshes);
});

// Ground floor special areas labels (use short names)
const shortAreaNames = {
  RECEPTION: 'Reception', BANQUET: 'Banquet Hall',
  GYM: 'Gym', GAME: 'Game Zone', STEAM: 'Steam Room',
};
GROUND_FLOOR.specialAreas.forEach((sa) => {
  if (sa.id.startsWith('PASSAGE')) return; // skip passages
  const shortName = shortAreaNames[sa.id] || sa.label;
  createLabel(sa.id, shortName, sa.x, sa.z, sa.w, sa.d, 0, gfFloorH, gfLabelMeshes);
});

// Upper floor showroom labels
const ufFloorH = BUILDING.floors['2'].height;
const ufElev = BUILDING.floors['2'].elevation;
UPPER_FLOOR.showrooms.forEach((sr) => {
  createLabel(sr.id, sr.label, sr.x, sr.z, sr.w, sr.d, ufElev, ufFloorH, ufLabelMeshes);
});

// Upper floor office labels
UPPER_FLOOR.offices.forEach((off) => {
  createLabel(off.id, off.label, off.x, off.z, off.w, off.d, ufElev, ufFloorH, ufLabelMeshes);
});

// Initially hide all labels (shown when a specific floor is selected)
gfLabelMeshes.forEach(m => m.setEnabled(false));
ufLabelMeshes.forEach(m => m.setEnabled(false));

// Glow filtering handled by customEmissiveColorSelector (set above)

// Scene-level performance optimizations
scene.skipPointerMovePicking = true;
scene.autoClearDepthAndStencil = true;
scene.blockfreeActiveMeshesAndRenderingGroups = true;
scene.blockMaterialDirtyMechanism = true;
scene.getAnimationRatio(); // warm up

// Hide loading screen
setTimeout(() => {
  document.getElementById('loading').style.opacity = '0';
  setTimeout(() => {
    document.getElementById('loading').style.display = 'none';
  }, 500);
}, 1000);

// ─── UNIT SELECTION ───
const unitInfo = document.getElementById('unitInfo');
generator.onUnitSelect = (meta) => {
  const typeLabel = meta.type === 'showroom' ? 'Showroom' : 'Office';
  const floorLabel = meta.unitId.startsWith('GF') ? 'Ground Floor (Double Height)' :
    meta.unitId.startsWith('F2') || meta.unitId.includes('_2') ? '2nd Floor' :
    meta.unitId.startsWith('F3') || meta.unitId.includes('_3') ? '3rd Floor' :
    meta.unitId.startsWith('F4') || meta.unitId.includes('_4') ? '4th Floor' : '';
  unitInfo.innerHTML = `
    <strong>${meta.label}</strong><br/>
    ${typeLabel} — Unit ${meta.unitId}<br/>
    ${floorLabel ? `Floor: ${floorLabel}<br/>` : ''}
    ${meta.dim ? `<strong>Size: ${meta.dim}</strong>` : ''}
  `;
};

// ─── FLOOR SELECTOR ───
let currentFloor = '0';
let showingAllFloors = true; // track whether all floors are visible
const compassInner = document.getElementById('compass-inner');

function updateLabelsForFloor(floor) {
  const floorNum = parseInt(floor);
  // GF labels visible when ground floor or basement selected
  const showGF = (floorNum <= 0);
  gfLabelMeshes.forEach(m => m.setEnabled(showGF));
  // UF labels visible when upper floors selected (2, 3, 4)
  const showUF = (floorNum >= 2);
  ufLabelMeshes.forEach(m => {
    m.setEnabled(showUF);
    if (showUF) {
      // Move labels to the correct floor elevation (slightly above floor)
      const floorData = BUILDING.floors[floor] || BUILDING.floors['2'];
      m.position.y = floorData.elevation + 0.5;
    }
  });
}

document.querySelectorAll('.floor-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('.floor-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');

    const floor = btn.dataset.floor;
    if (floor === 'all') {
      // Show all floors — full building view (labels hidden since they'd be inside)
      showingAllFloors = true;
      currentFloor = '0';
      generator.showAllFloors();
      gfLabelMeshes.forEach(m => m.setEnabled(false));
      ufLabelMeshes.forEach(m => m.setEnabled(false));
      if (activeCamera === orbitalCam) {
        orbitalCam.target.y = 20;
      }
      return;
    }

    showingAllFloors = false;
    currentFloor = floor;
    generator.showFloor(currentFloor);
    updateLabelsForFloor(currentFloor);

    const floorData = BUILDING.floors[currentFloor];
    if (floorData) {
      if (activeCamera === orbitalCam) {
        orbitalCam.target.y = floorData.elevation + floorData.height / 2;
      } else if (activeCamera === walkCam) {
        walkCam.position.y = floorData.elevation + 5.6;
      }
    }
  });
});

// ─── VIEW TOGGLES ───
function setCamera(cam) {
  activeCamera = cam;
  scene.activeCamera = cam;
  cam.attachControl(canvas, true);
}

document.getElementById('btn-orbital').addEventListener('click', () => {
  document.querySelectorAll('.view-btn').forEach(b => b.classList.remove('active'));
  document.getElementById('btn-orbital').classList.add('active');
  walkCam.detachControl();
  setCamera(orbitalCam);
  // Respect current floor isolation; only show all if "All Floors" was selected
  if (showingAllFloors) {
    generator.showAllFloors();
  } else {
    generator.showFloor(currentFloor);
  }
});

document.getElementById('btn-walkthrough').addEventListener('click', () => {
  document.querySelectorAll('.view-btn').forEach(b => b.classList.remove('active'));
  document.getElementById('btn-walkthrough').classList.add('active');
  orbitalCam.detachControl();

  const floorData = BUILDING.floors[currentFloor];
  walkCam.position = new Vector3(144, (floorData?.elevation || 0) + 5.6, 195);
  walkCam.setTarget(new Vector3(144, (floorData?.elevation || 0) + 5.6, 165));
  setCamera(walkCam);
  generator.showFloor(currentFloor);
});

// ─── COMPASS FINE-TUNE ───
// Adjust this value (degrees) if compass is still slightly off. Default 0.
const COMPASS_OFFSET_DEG = 0;

// ─── RENDER LOOP + LIVE COMPASS ───
// Camera positions (unchanged by RHS):
//   alpha=0 → camera at +X (East), facing West
//   alpha=PI/2 → camera at +Z (South), facing North
// Direction mapping (RHS): +X=East, +Z=South
let _camSaveTimer = 0;
engine.runRenderLoop(() => {
  // Save camera state every 500ms
  const now = performance.now();
  if (now - _camSaveTimer > 500) {
    _camSaveTimer = now;
    const t = orbitalCam.target;
    localStorage.setItem('bb2_camera', JSON.stringify({
      alpha: orbitalCam.alpha, beta: orbitalCam.beta, radius: orbitalCam.radius,
      tx: t.x, ty: t.y, tz: t.z,
    }));
  }

  let compassDeg = 0;
  if (activeCamera === orbitalCam) {
    // RHS: alpha=0→camera at +X (East), faces West (compassDeg=90)
    //       alpha=PI/2→camera at +Z (South), faces North (compassDeg=0)
    compassDeg = (Math.PI / 2 - orbitalCam.alpha) * 180 / Math.PI + COMPASS_OFFSET_DEG;
  } else if (activeCamera === walkCam) {
    // RHS: rotation.y=0→faces -Z (North). compassDeg = rotation.y in degrees.
    compassDeg = walkCam.rotation.y * 180 / Math.PI + COMPASS_OFFSET_DEG;
  }
  compassInner.style.transform = `rotate(${compassDeg}deg)`;

  scene.render();
});

window.addEventListener('resize', () => {
  engine.resize();
});

// ─── KEYBOARD SHORTCUTS ───
window.addEventListener('keydown', (e) => {
  const floorMap = { '1': '-2', '2': '-1', '3': '0', '4': '2', '5': '3', '6': '4' };
  if (floorMap[e.key]) {
    const btn = document.querySelector(`.floor-btn[data-floor="${floorMap[e.key]}"]`);
    if (btn) btn.click();
  }
  if (e.key === 'v' || e.key === 'V') {
    if (activeCamera === orbitalCam) {
      document.getElementById('btn-walkthrough').click();
    } else {
      document.getElementById('btn-orbital').click();
    }
  }
});

// ─── 2D FLOOR PLAN EDITOR ───
function rebuildLabels() {
  // Dispose existing labels
  [...gfLabelMeshes, ...ufLabelMeshes].forEach(m => {
    if (m.material) {
      if (m.material.emissiveTexture) m.material.emissiveTexture.dispose();
      m.material.dispose();
    }
    m.dispose();
  });
  gfLabelMeshes.length = 0;
  ufLabelMeshes.length = 0;

  // Recreate GF labels
  GROUND_FLOOR.showrooms.forEach((sr) => {
    createLabel(sr.id, sr.label, sr.x, sr.z, sr.w, sr.d, 0, gfFloorH, gfLabelMeshes);
  });
  GROUND_FLOOR.specialAreas.forEach((sa) => {
    if (sa.id.startsWith('PASSAGE')) return;
    const shortName = shortAreaNames[sa.id] || sa.label;
    createLabel(sa.id, shortName, sa.x, sa.z, sa.w, sa.d, 0, gfFloorH, gfLabelMeshes);
  });

  // Recreate UF labels
  UPPER_FLOOR.showrooms.forEach((sr) => {
    createLabel(sr.id, sr.label, sr.x, sr.z, sr.w, sr.d, ufElev, ufFloorH, ufLabelMeshes);
  });
  UPPER_FLOOR.offices.forEach((off) => {
    createLabel(off.id, off.label, off.x, off.z, off.w, off.d, ufElev, ufFloorH, ufLabelMeshes);
  });

  gfLabelMeshes.forEach(m => m.setEnabled(false));
  ufLabelMeshes.forEach(m => m.setEnabled(false));
  // No need to exclude from glow — using includedOnlyMesh mode
}

const editor = new FloorPlanEditor({
  onApply: () => {
    // Temporarily disable scene-level optimization flags during rebuild
    scene.blockfreeActiveMeshesAndRenderingGroups = false;
    scene.blockMaterialDirtyMechanism = false;

    // Dispose existing floor node children
    Object.values(generator.floorNodes).forEach(node => {
      node.getChildMeshes().forEach(m => {
        if (m.material && !m.material.name.startsWith('shared_')) {
          // Don't dispose shared materials
        }
        m.dispose();
      });
      node.dispose();
    });
    generator.floorNodes = {};
    generator.unitMeshes = [];

    // Rebuild floors from updated data
    generator._createAllFloors();

    // Rebuild exterior features (parking, planters, ground pad)
    generator.rebuildExterior();

    // Re-freeze static meshes
    generator._freezeStaticMeshes();

    // Rebuild labels
    rebuildLabels();

    // Reset floor view
    if (showingAllFloors) {
      generator.showAllFloors();
    } else {
      generator.showFloor(currentFloor);
      updateLabelsForFloor(currentFloor);
    }

    // Re-enable scene-level optimization flags
    scene.blockfreeActiveMeshesAndRenderingGroups = true;
    scene.blockMaterialDirtyMechanism = true;
  },
});

document.getElementById('btn-editor').addEventListener('click', () => {
  // Hide 3D UI elements
  canvas.style.display = 'none';
  document.getElementById('floor-selector').style.display = 'none';
  document.getElementById('info-panel').style.display = 'none';
  document.getElementById('controls-help').style.display = 'none';
  document.getElementById('view-toggle').style.display = 'none';
  document.getElementById('compass').style.display = 'none';

  // Re-clone current data so editor starts fresh from live model
  editor.data = {
    ground: {
      showrooms: JSON.parse(JSON.stringify(GROUND_FLOOR.showrooms)),
      specialAreas: JSON.parse(JSON.stringify(GROUND_FLOOR.specialAreas)),
    },
    upper: {
      showrooms: JSON.parse(JSON.stringify(UPPER_FLOOR.showrooms)),
      offices: JSON.parse(JSON.stringify(UPPER_FLOOR.offices)),
    },
  };

  // Re-clone exterior data
  editor.groundPadVertices = JSON.parse(JSON.stringify(GROUND_PAD_OUTLINE));
  editor.parkingGroups = JSON.parse(JSON.stringify(PARKING_GROUPS));
  editor.planters = JSON.parse(JSON.stringify(PLACED_PLANTERS));
  editor.roads = JSON.parse(JSON.stringify(PLACED_ROADS));

  editor.open();
});

// Expose for debugging
window.__scene = scene;
window.__cameras = { orbitalCam, walkCam };

// ─── DEBUG: Alt+Click any mesh to identify it, Shift+Click to delete it ───
scene.onPointerObservable.add((pointerInfo) => {
  if (pointerInfo.type !== 16 /* PointerEventTypes.POINTERPICK */) return;
  const evt = pointerInfo.event;
  if (!evt.altKey && !evt.shiftKey) return;
  const picked = pointerInfo.pickInfo;
  if (!picked.hit || !picked.pickedMesh) return;
  const mesh = picked.pickedMesh;
  const p = mesh.position;
  const matName = mesh.material ? mesh.material.name : 'none';
  const parent = mesh.parent ? mesh.parent.name : 'none';
  console.log(`[PICK] name="${mesh.name}" mat=${matName} parent=${parent} pos=(${p.x.toFixed(1)}, ${p.y.toFixed(1)}, ${p.z.toFixed(1)})`);
  if (evt.shiftKey) {
    console.log(`[DELETE] Removing mesh: ${mesh.name}`);
    mesh.dispose();
  }
});

// ─── SIGN MOVER: Press M to toggle, then use arrow keys to position the sign ───
{
  let signMoveMode = false;
  const step = 10.0;      // normal step (feet)
  const fineStep = 2.0;   // shift-held step (feet)
  const rotStep = 0.1;    // rotation step (radians, ~6 degrees)

  const logSignPos = () => {
    const sr = scene.getTransformNodeByName('signRoot');
    if (!sr) return;
    const p = sr.position;
    const r = sr.rotation.y;
    console.log(`[SIGN POS] x=${p.x.toFixed(2)}, y=${p.y.toFixed(2)}, z=${p.z.toFixed(2)}, rotation.y=${r.toFixed(4)} (${(r * 180 / Math.PI).toFixed(1)} deg)`);
  };

  // Use Babylon's keyboard observable — guaranteed to work inside the engine
  scene.onKeyboardObservable.add((kbInfo) => {
    if (kbInfo.type !== 1 /* KeyboardEventTypes.KEYDOWN */) return;
    const key = kbInfo.event.key;
    const shift = kbInfo.event.shiftKey;

    // Toggle sign move mode with M key
    if (key === 'm' || key === 'M') {
      signMoveMode = !signMoveMode;
      const cam = scene.activeCamera;
      if (signMoveMode) {
        if (cam && cam.inputs && cam.inputs.attached.keyboard)
          cam.inputs.attached.keyboard.detachControl();
        // Unfreeze sign meshes so position changes are visible
        const sr = scene.getTransformNodeByName('signRoot');
        if (sr) sr.getChildMeshes().forEach(child => child.unfreezeWorldMatrix());
        console.log('%c[SIGN MOVER] ON — Arrow keys: move X/Z, PageUp/Down: height, [ ]: rotate, Shift: fine. Press M to finish.',
          'color: #0f0; font-size: 14px; font-weight: bold');
        logSignPos();
      } else {
        if (cam && cam.inputs && cam.inputs.attached.keyboard)
          cam.inputs.attached.keyboard.attachControl(canvas);
        // Re-freeze sign meshes for performance
        const sr = scene.getTransformNodeByName('signRoot');
        if (sr) sr.getChildMeshes().forEach(child => child.freezeWorldMatrix());
        console.log('%c[SIGN MOVER] OFF — Final position:', 'color: #f80; font-size: 14px; font-weight: bold');
        logSignPos();
      }
      return;
    }

    if (!signMoveMode) return;

    const sr = scene.getTransformNodeByName('signRoot');
    if (!sr) { console.warn('[SIGN MOVER] signRoot not found'); return; }

    const s = shift ? fineStep : step;
    switch (key) {
      case 'ArrowLeft':   sr.position.x -= s; break;
      case 'ArrowRight':  sr.position.x += s; break;
      case 'ArrowUp':     sr.position.z -= s; break;
      case 'ArrowDown':   sr.position.z += s; break;
      case 'PageUp':      sr.position.y += s; break;
      case 'PageDown':    sr.position.y -= s; break;
      case '[':           sr.rotation.y -= rotStep; break;
      case ']':           sr.rotation.y += rotStep; break;
      default: return;
    }
    // Force Babylon to recalculate transforms for signRoot + all letter meshes
    sr.computeWorldMatrix(true);
    sr.getChildMeshes().forEach(child => child.computeWorldMatrix(true));
    logSignPos();
  });
}
